// src/components/Projects/ProjectListView.tsx
import { useState, useMemo } from 'react';
import { Plus, ChevronDown, ChevronRight, Pencil, Check, X, Trash2, SlidersHorizontal, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useProjectSections } from '@/hooks/useProjectSections';
import { useProjectTasks } from '@/hooks/useProjectTasks';
import { useCompanyUsers } from '@/hooks/useCompanyUsers';
import { useTask } from '@/hooks/useTask';
import { useTasks } from '@/hooks/useTasks';
import { normalizeFieldValue } from '@/hooks/useTaskCustomFields';
import CreateProjectTaskDialog from './CreateProjectTaskDialog';
import { TaskDetailSheet } from '@/components/Tasks/TaskDetailSheet';
import type { ProjectTask, ProjectSection } from '@/types/project';

interface ProjectListViewProps {
  projectId: string;
  showCompleted: boolean;
  canEdit: boolean;
}

const PRIORITY_LABELS: Record<string, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'text-red-600',
  high: 'text-orange-500',
  medium: 'text-yellow-500',
  low: 'text-blue-400',
};

const PRIORITY_DOT: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-amber-400',
  low: 'bg-blue-400',
};

const INTERNAL_FIELDS = ['auto_type', 'linked_sale_id', 'linked_loan_id'];

// Module-level helper — no closure needed (only uses imported normalizeFieldValue)
function renderCustomFieldCell(
  customFields: Record<string, any> | null | undefined,
  fieldName: string
): React.ReactNode {
  if (!customFields || !(fieldName in customFields)) {
    return <span className="text-xs text-muted-foreground/50">—</span>;
  }
  const entry = normalizeFieldValue((customFields as Record<string, any>)[fieldName]);
  switch (entry.type) {
    case 'number':
      return (
        <span className="text-xs text-muted-foreground">
          {entry.value != null ? Number(entry.value).toLocaleString('pt-BR') : '—'}
        </span>
      );
    case 'date':
      return (
        <span className="text-xs text-muted-foreground">
          {entry.value
            ? new Date(String(entry.value) + 'T12:00:00')
                .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
            : '—'}
        </span>
      );
    case 'checkbox':
      return (
        <span className={`text-xs font-medium ${entry.value ? 'text-emerald-600' : 'text-muted-foreground'}`}>
          {entry.value ? 'Sim' : 'Não'}
        </span>
      );
    default:
      return (
        <span className="text-xs text-muted-foreground truncate max-w-[7rem]">
          {String(entry.value ?? '—')}
        </span>
      );
  }
}

function TaskRow({
  task,
  assigneeName,
  onTaskClick,
  visibleCustomColumns,
  onDeleteTask,
}: {
  task: ProjectTask;
  assigneeName: string | null;
  onTaskClick: (task: ProjectTask) => void;
  visibleCustomColumns: string[];
  onDeleteTask?: (taskId: string) => void;
}) {
  const isCompleted = task.status === 'completed';

  return (
    <div
      className="relative group flex items-center py-2 hover:bg-muted/50 transition-colors cursor-pointer border-b border-border/30"
      onClick={() => onTaskClick(task)}
    >
      {/* Título + dot */}
      <div className="flex-1 flex items-center gap-2 px-3 min-w-0">
        <div
          className={`h-2 w-2 rounded-full flex-shrink-0 ${
            isCompleted
              ? 'bg-green-500'
              : (PRIORITY_DOT[task.priority ?? ''] ?? 'bg-muted-foreground/40')
          }`}
        />
        <span
          className={`text-sm truncate ${
            isCompleted ? 'line-through text-muted-foreground' : ''
          }`}
        >
          {task.title}
        </span>
      </div>

      {/* Responsável */}
      <div className="hidden sm:flex items-center w-32 px-3 border-l border-border/40 shrink-0 min-h-[2rem]">
        {assigneeName && (
          <span className="text-xs text-muted-foreground truncate">{assigneeName}</span>
        )}
      </div>

      {/* Prazo */}
      <div className="hidden md:flex items-center w-24 px-3 border-l border-border/40 shrink-0 min-h-[2rem]">
        {task.due_date && (
          <span className="text-xs text-muted-foreground">
            {new Date(task.due_date + 'T12:00:00').toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: 'short',
            })}
          </span>
        )}
      </div>

      {/* Prioridade */}
      <div className="flex items-center w-20 px-3 border-l border-border/40 shrink-0 min-h-[2rem]">
        {task.priority && (
          <span
            className={`text-xs font-medium ${
              PRIORITY_COLORS[task.priority] ?? 'text-muted-foreground'
            }`}
          >
            {PRIORITY_LABELS[task.priority] ?? task.priority}
          </span>
        )}
      </div>

      {/* Colunas de campos personalizados */}
      {visibleCustomColumns.map(colName => (
        <div
          key={colName}
          className="hidden lg:flex items-center w-32 px-3 border-l border-border/40 shrink-0 min-h-[2rem]"
        >
          {renderCustomFieldCell(task.custom_fields, colName)}
        </div>
      ))}

      {/* Menu de contexto ⋮ */}
      {onDeleteTask && (
        <div
          className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onDeleteTask(task.id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir tarefa
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

function SectionBlock({
  section,
  tasks,
  canEdit,
  projectId,
  getAssigneeName,
  onTaskClick,
  onRenameSection,
  onDeleteSection,
  onDeleteTask,
  visibleCustomColumns,
}: {
  section: ProjectSection;
  tasks: ProjectTask[];
  canEdit: boolean;
  projectId: string;
  getAssigneeName: (userId: string | null) => string | null;
  onTaskClick: (task: ProjectTask) => void;
  onRenameSection: (sectionId: string, name: string) => Promise<void>;
  onDeleteSection?: (sectionId: string) => void;
  onDeleteTask?: (taskId: string) => void;
  visibleCustomColumns: string[];
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(section.name);

  const handleRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === section.name) { setIsRenaming(false); return; }
    try {
      await onRenameSection(section.id, trimmed);
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <div className="mb-4">
      {/* Cabeçalho da seção */}
      <div className="group flex items-center gap-2 mb-1 py-1">
        <button
          className="text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => setCollapsed((v) => !v)}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {isRenaming ? (
          <div className="flex items-center gap-1 flex-1" onClick={e => e.stopPropagation()}>
            <Input
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleRename();
                if (e.key === 'Escape') { setIsRenaming(false); setRenameValue(section.name); }
              }}
              className="h-6 text-sm py-0 px-2 flex-1"
              autoFocus
            />
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-green-600 hover:text-green-700" onClick={handleRename}>
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setIsRenaming(false); setRenameValue(section.name); }}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <>
            <span className="text-sm font-medium">{section.name}</span>
            {canEdit && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-muted-foreground"
                  onClick={e => { e.stopPropagation(); setIsRenaming(true); setRenameValue(section.name); }}
                  title="Renomear seção"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                  onClick={e => { e.stopPropagation(); onDeleteSection?.(section.id); }}
                  title="Excluir seção"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </>
        )}

        <span className="text-xs text-muted-foreground">
          {tasks.length}
        </span>

        {canEdit && !isRenaming && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 ml-auto text-muted-foreground"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Tarefas */}
      {!collapsed && (
        <div className="ml-6 border-l border-border pl-3">
          {tasks.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2 italic">
              Nenhuma tarefa nesta seção.
            </p>
          ) : (
            tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                assigneeName={getAssigneeName(task.assigned_to)}
                onTaskClick={onTaskClick}
                visibleCustomColumns={visibleCustomColumns}
                onDeleteTask={onDeleteTask}
              />
            ))
          )}

          {canEdit && (
            <button
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-2 mt-1"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Adicionar tarefa
            </button>
          )}
        </div>
      )}

      <CreateProjectTaskDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        projectId={projectId}
        sectionId={section.id}
        sectionName={section.name}
      />
    </div>
  );
}

export default function ProjectListView({
  projectId,
  showCompleted,
  canEdit,
}: ProjectListViewProps) {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [addingSectionOpen, setAddingSectionOpen] = useState(false);
  const [addingSectionName, setAddingSectionName] = useState('');
  const [deletingSectionId, setDeletingSectionId] = useState<string | null>(null);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);
  const [visibleCustomColumns, setVisibleCustomColumns] = useState<string[]>([]);

  const { sections, isLoading: sectionsLoading, createSection, renameSection, deleteSection } = useProjectSections(projectId);
  const { tasks: allProjectTasks, tasksBySection, isLoading: tasksLoading } = useProjectTasks(
    projectId,
    showCompleted
  );
  const { data: selectedTask = null } = useTask(selectedTaskId);
  const { users } = useCompanyUsers();
  const { deleteTask } = useTasks();

  const availableCustomColumns = useMemo(() => {
    const names = new Set<string>();
    allProjectTasks.forEach(task => {
      const cf = task.custom_fields;
      if (cf && typeof cf === 'object') {
        Object.keys(cf as Record<string, any>)
          .filter(k => !INTERNAL_FIELDS.includes(k))
          .forEach(k => names.add(k));
      }
    });
    return Array.from(names).sort();
  }, [allProjectTasks]);

  const getAssigneeName = (userId: string | null): string | null => {
    if (!userId) return null;
    return users.find((u) => u.id === userId)?.full_name ?? null;
  };

  const handleConfirmDeleteSection = () => {
    if (deletingSectionId) {
      deleteSection(deletingSectionId);
      setDeletingSectionId(null);
    }
  };

  const handleCreateSection = async () => {
    if (!addingSectionName.trim()) return;
    try {
      await createSection({ name: addingSectionName.trim() });
      setAddingSectionName('');
      setAddingSectionOpen(false);
    } catch {
      setAddingSectionOpen(false);
    }
  };

  if (sectionsLoading || tasksLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-muted-foreground">
          Este projeto ainda não tem seções.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Seções são criadas automaticamente ao criar a primeira tarefa, ou pelo Quadro.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Cabeçalho da tabela */}
      <div className="flex items-center py-1.5 mb-2 text-xs font-medium text-muted-foreground border-b border-border">
        <span className="flex-1 px-3">Título</span>
        <span className="hidden sm:flex items-center w-32 px-3 border-l border-border/60">Responsável</span>
        <span className="hidden md:flex items-center w-24 px-3 border-l border-border/60">Prazo</span>
        <span className="flex items-center w-20 px-3 border-l border-border/60">Prioridade</span>

        {/* Colunas de campos personalizados visíveis */}
        {visibleCustomColumns.map(colName => (
          <span
            key={colName}
            className="hidden lg:flex items-center w-32 px-3 border-l border-border/60"
          >
            <span className="truncate">{colName}</span>
          </span>
        ))}

        {/* Botão de gerenciar colunas — só aparece quando há campos disponíveis */}
        {availableCustomColumns.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="flex items-center justify-center w-8 h-7 ml-1 rounded
                           text-muted-foreground hover:text-foreground hover:bg-muted/60
                           transition-colors flex-shrink-0"
                title="Gerenciar colunas"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-2" align="end">
              <p className="text-xs font-medium text-muted-foreground mb-2 px-1">
                Campos Personalizados
              </p>
              {availableCustomColumns.map(colName => (
                <div
                  key={colName}
                  className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-muted/40 cursor-pointer"
                  onClick={() =>
                    setVisibleCustomColumns(prev =>
                      prev.includes(colName)
                        ? prev.filter(n => n !== colName)
                        : [...prev, colName]
                    )
                  }
                >
                  <Checkbox
                    checked={visibleCustomColumns.includes(colName)}
                    className="pointer-events-none"
                  />
                  <span className="text-sm truncate">{colName}</span>
                </div>
              ))}
            </PopoverContent>
          </Popover>
        )}
      </div>

      {sections.map((section) => (
        <SectionBlock
          key={section.id}
          section={section}
          tasks={tasksBySection(section.id)}
          canEdit={canEdit}
          projectId={projectId}
          getAssigneeName={getAssigneeName}
          onTaskClick={(t) => setSelectedTaskId(t.id)}
          onRenameSection={async (id, name) => renameSection({ sectionId: id, name })}
          onDeleteSection={(id) => setDeletingSectionId(id)}
          onDeleteTask={canEdit ? (taskId) => setDeletingTaskId(taskId) : undefined}
          visibleCustomColumns={visibleCustomColumns}
        />
      ))}

      {canEdit && (
        <div className="mt-4 px-1">
          {addingSectionOpen ? (
            <div className="flex items-center gap-2 py-1">
              <Input
                value={addingSectionName}
                onChange={e => setAddingSectionName(e.target.value)}
                placeholder="Nome da seção..."
                className="h-7 text-sm max-w-xs"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCreateSection();
                  if (e.key === 'Escape') {
                    setAddingSectionOpen(false);
                    setAddingSectionName('');
                  }
                }}
              />
              <Button size="sm" className="h-7" onClick={handleCreateSection}>
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7"
                onClick={() => { setAddingSectionOpen(false); setAddingSectionName(''); }}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <button
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
              onClick={() => setAddingSectionOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Adicionar seção
            </button>
          )}
        </div>
      )}

      {/* AlertDialog — excluir seção */}
      <AlertDialog
        open={!!deletingSectionId}
        onOpenChange={(open) => { if (!open) setDeletingSectionId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir seção?</AlertDialogTitle>
            <AlertDialogDescription>
              As tarefas desta seção não serão excluídas — continuarão visíveis sem seção.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteSection}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog — excluir tarefa */}
      <AlertDialog
        open={!!deletingTaskId}
        onOpenChange={(open) => { if (!open) setDeletingTaskId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir tarefa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta tarefa será excluída permanentemente.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deletingTaskId) {
                  deleteTask(deletingTaskId);
                  setDeletingTaskId(null);
                }
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TaskDetailSheet
        task={selectedTask}
        open={!!selectedTask && !!selectedTaskId}
        onOpenChange={(open) => { if (!open) setSelectedTaskId(null); }}
      />
    </div>
  );
}
